import { useRef, useState } from 'react';

import { Icon } from '../../components/Icon';
import { ConfirmationDialog } from '../../components/ConfirmationDialog';
import type { TableSourceDescriptor } from '../../data/model';
import { AnchoredMenu } from './AnchoredMenu';
import { focusAfterListItemRemoval } from './focusAfterRemoval';

interface TableRowProps {
  readonly source: TableSourceDescriptor;
  readonly selected: boolean;
  readonly removable: boolean;
  readonly exportAvailable: boolean;
  readonly onExport: () => Promise<void>;
  readonly onRemove: () => void;
  readonly onViewData: () => void;
  readonly removalUsage?:
    | {
        readonly joinCount: number;
        readonly layerNames: readonly string[];
      }
    | undefined;
}

function listNames(names: readonly string[]): string {
  if (names.length < 2) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

export function TableRow({
  source,
  selected,
  removable,
  exportAvailable,
  onExport,
  onRemove,
  onViewData,
  removalUsage,
}: TableRowProps) {
  const [open, setOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [dialog, setDialog] = useState<{
    readonly kind: 'export-error' | 'in-use' | 'remove';
    readonly returnFocusTo: HTMLElement;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const rowRef = useRef<HTMLLIElement | null>(null);

  return (
    <li className={`table-item${selected ? ' table-item-selected' : ''}`} ref={rowRef}>
      <button
        aria-pressed={selected}
        className="table-row-main"
        data-list-item-primary="true"
        onClick={onViewData}
        type="button"
      >
        <Icon name="table" />
        <span title={source.name}>{source.name}</span>
      </button>
      <div className="layer-actions">
        <button
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={`Actions for table ${source.name}`}
          className="icon-button layer-actions-trigger"
          onClick={() => setOpen((current) => !current)}
          ref={triggerRef}
          type="button"
        >
          <Icon name="more" />
        </button>
        <AnchoredMenu
          className="row-actions-menu"
          anchorRef={triggerRef}
          label={`Actions for table ${source.name}`}
          onDismiss={() => setOpen(false)}
          open={open}
        >
          <button
            onClick={() => {
              setOpen(false);
              onViewData();
            }}
            role="menuitem"
            type="button"
          >
            <Icon name="table" /> View data
          </button>
          {exportAvailable ? (
            <button
              disabled={preparing}
              onClick={async () => {
                const returnFocusTo = triggerRef.current;
                setOpen(false);
                setPreparing(true);
                try {
                  await onExport();
                } catch {
                  if (returnFocusTo) setDialog({ kind: 'export-error', returnFocusTo });
                } finally {
                  setPreparing(false);
                }
              }}
              role="menuitem"
              type="button"
            >
              <Icon name="download" /> {preparing ? 'Preparing…' : 'Export CSV'}
            </button>
          ) : null}
          {removable ? (
            <button
              className="menu-danger"
              onClick={() => {
                setOpen(false);
                if (triggerRef.current) {
                  setDialog({
                    kind: removalUsage ? 'in-use' : 'remove',
                    returnFocusTo: triggerRef.current,
                  });
                }
              }}
              role="menuitem"
              type="button"
            >
              <Icon name="trash" /> Remove table
            </button>
          ) : null}
        </AnchoredMenu>
      </div>
      {dialog?.kind === 'remove' ? (
        <ConfirmationDialog
          confirmLabel="Remove table"
          confirmTone="danger"
          description={`“${source.name}” will be removed from this project.`}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            focusAfterListItemRemoval(rowRef.current, onRemove);
            setDialog(null);
          }}
          returnFocusTo={dialog.returnFocusTo}
          title="Remove table?"
        />
      ) : null}
      {dialog?.kind === 'in-use' && removalUsage ? (
        <ConfirmationDialog
          cancelLabel={null}
          confirmLabel="Close"
          description={
            <>
              <p>
                “{source.name}” is used by {removalUsage.joinCount.toLocaleString()}{' '}
                {removalUsage.joinCount === 1 ? 'join' : 'joins'}{' '}
                {removalUsage.layerNames.length === 1 ? 'on' : 'across'}{' '}
                {listNames(removalUsage.layerNames)}.
              </p>
              <p>
                Remove {removalUsage.joinCount === 1 ? 'that join' : 'those joins'} before deleting
                the table.
              </p>
            </>
          }
          onCancel={() => setDialog(null)}
          onConfirm={() => setDialog(null)}
          returnFocusTo={dialog.returnFocusTo}
          role="dialog"
          title="Table is currently in use"
        />
      ) : null}
      {dialog?.kind === 'export-error' ? (
        <ConfirmationDialog
          cancelLabel={null}
          confirmLabel="Close"
          description="Could not prepare the CSV export."
          onCancel={() => setDialog(null)}
          onConfirm={() => setDialog(null)}
          returnFocusTo={dialog.returnFocusTo}
          role="dialog"
          title="Export failed"
        />
      ) : null}
    </li>
  );
}
